import React, { useState } from 'react';

import { AchadoDoAssistente } from '../../api/radarApi';
import ComoFunciona from '../como-funciona/ComoFunciona';
import '../como-funciona/help-circle-btn.css';
import EsqueletoDeCard from './EsqueletoDeCard';
import RadarCard from './RadarCard';
import VotoDoAchado from './VotoDoAchado';
import { secoesDaProveniencia } from './secoesDaProveniencia';
import { useAchadosDoAssistente } from './useAchadosDoAssistente';
import './radar.css';
import '../../shared/feedback/erro-de-tela.css';

interface Props {
  /** O id do radar no catálogo do backend (`financeiro`, `relacionamento`). */
  radarId: string;
  titulo?: string;
  /** O texto que explica, no vocabulário daquele público, o que a seção traz. */
  apoio: React.ReactNode;
  /** Ações do cartão ("Ver linhas", "Ver associados"). Devolver `null` = cartão sem ação. */
  renderAcoes?: (achado: AchadoDoAssistente) => React.ReactNode;
  /** O que dizer sobre a janela além do que a resposta declara — ex.: que ela ignora o filtro. */
  notaDaJanela?: React.ReactNode;
  /** A seção "Como conferir" do `?` de proveniência, quando o radar tem onde conferir. */
  comoConferir?: React.ReactNode;
  /** `h2` numa página inteira de radar; `h3` num painel que já está dentro de outra tela. */
  nivelDoTitulo?: 'h2' | 'h3';
}

const SELO_CANDIDATO = { rotulo: 'Candidato', classe: 'radar-badge-candidato' };
const SELO_ACOMPANHAMENTO = { rotulo: 'Aprovado pela equipe', classe: 'radar-badge-aprovado' };

/**
 * A seção "achados do assistente" de QUALQUER radar (transversal/at-82, ADR 0046).
 *
 * Substitui as duas cópias que existiam (a página do Radar Financeiro e o painel do Radar do
 * Relacionamento), que já haviam divergido em três pontos — a posição do selo, o formato do número
 * e os textos de estado.
 *
 * ## Os cinco estados que NÃO podem se parecer
 *
 * Esta é a seção em que a regra da at-60 vale em dobro, porque "o assistente não propôs achados
 * hoje" é um desfecho **legítimo e frequente**. Falha de carga, exploração quebrada, exploração em
 * curso, IA desligada no ambiente e "olhou e não achou nada" levam a conclusões diferentes, e a
 * tela precisa dizer em qual delas está. Colapsar dois deles faz a tela afirmar que está tudo bem
 * sobre uma verificação que não aconteceu.
 *
 * ## A janela é fixa, e isso é dito na própria seção
 *
 * 🔴 Os achados do assistente olham a **janela padrão do radar**, não o filtro da tela (análise
 * §3.3): sem um conjunto por dia igual para todo mundo, duas pessoas com filtros diferentes
 * votariam em achados diferentes e o voto não significaria nada. A hint fica **fixa na seção**,
 * não só no drawer — é a leitura errada mais provável de quem acabou de aplicar um período.
 */
const SecaoAchadosDoAssistente: React.FC<Props> = ({
  radarId,
  titulo = 'Achados do assistente',
  apoio,
  renderAcoes,
  notaDaJanela,
  comoConferir,
  nivelDoTitulo = 'h2',
}) => {
  const { dados, carregando, erro, esperaEsgotada } = useAchadosDoAssistente(radarId);
  const [achadoExplicado, setAchadoExplicado] = useState<AchadoDoAssistente | null>(null);

  const Titulo = nivelDoTitulo;
  const estado = dados?.exploracao.estado;
  const disponivel = dados?.disponivel ?? false;
  const emAndamento = estado === 'EM_ANDAMENTO';

  const acompanhamentos = (dados?.achados ?? []).filter((a) => a.origem === 'ACOMPANHAMENTO');
  // Sem `origem` (backend antigo na janela de deploy) o achado é candidato — nunca some da tela.
  const candidatos = (dados?.achados ?? []).filter((a) => a.origem !== 'ACOMPANHAMENTO');

  const cartaoDe = (achado: AchadoDoAssistente, acompanhamento: boolean) => (
    <RadarCard
      key={achado.id}
      titulo={achado.titulo}
      numero={achado.numero}
      formato={achado.formato}
      unidade={achado.unidade}
      texto={achado.texto}
      selo={acompanhamento ? SELO_ACOMPANHAMENTO : SELO_CANDIDATO}
      onExplicar={() => setAchadoExplicado(achado)}
      acoes={renderAcoes?.(achado)}
      rodape={
        <>
          {achado.aparicoes > 1 && (
            <p className="radar-aparicoes">
              Este recorte já apareceu em {achado.aparicoes} dias.
            </p>
          )}
          <VotoDoAchado radarId={radarId} achadoId={achado.id} meuVoto={achado.meuVoto} />
        </>
      }
    />
  );

  return (
    <section className="radar-candidatos-secao">
      <Titulo className="radar-candidatos-titulo">{titulo}</Titulo>
      <p className="radar-candidatos-apoio">{apoio}</p>

      {/*
        🔴 A janela FIXA, declarada na seção e não só no drawer. Um achado analisado sobre doze
        meses, exibido numa tela filtrada por três, é lido como sendo dos três — e o número não
        bate com nada que a pessoa consiga conferir.
      */}
      {dados && disponivel && (
        <p className="radar-janela-fixa">
          Analisado em <strong>{dados.diaReferencia}</strong> sobre {dados.janela.descricao}. Todos
          que veem este radar avaliam os mesmos achados até o dia seguinte.
          {notaDaJanela ? <> {notaDaJanela}</> : null}
        </p>
      )}

      {(carregando || emAndamento) && !erro && (
        <div
          className="radar-cards"
          role="status"
          aria-label="O assistente está analisando os dados"
        >
          <EsqueletoDeCard />
          <EsqueletoDeCard />
        </div>
      )}

      {!carregando && erro && (
        <p className="erro-de-tela">
          Não foi possível carregar os achados do assistente. Esta seção não está afirmando que não
          há achados — ela não conseguiu verificar. Os demais números da tela não são afetados.
        </p>
      )}

      {!carregando && !erro && dados && !disponivel && (
        <p className="radar-candidatos-indisponivel">
          A busca por achados do assistente não está configurada neste ambiente. Os demais números
          da tela não dependem dela.
        </p>
      )}

      {!carregando && !erro && dados && disponivel && (
        <>
          {/*
            🔴 at-83 — DESATIVADO tem bloco PRÓPRIO, e nunca o de `FALHOU` nem o de vazio.
            "A administração desligou este Radar" e "a exploração quebrou" mandam o usuário a
            lugares opostos: o primeiro é uma decisão que alguém tomou e sabe desfazer; o segundo
            manda acionar a TI. Por isso o tom aqui é neutro — não é erro — e o texto nomeia quem
            desativou, que é a informação que permite cobrar a religação.
          */}
          {estado === 'DESATIVADO' && (
            <p className="radar-candidatos-indisponivel">
              O Radar está desativado pela Administração do Sistema.
              {dados.desativacao?.por
                ? ` Desativado por ${dados.desativacao.por}${
                    dados.desativacao.em
                      ? ` em ${new Date(dados.desativacao.em).toLocaleDateString('pt-BR')}`
                      : ''
                  }.`
                : ''}{' '}
              Enquanto estiver assim, nenhuma análise é executada.
            </p>
          )}

          {/*
            "A exploração falhou" e "o assistente não propôs nada" são coisas diferentes: a
            primeira é uma verificação que não aconteceu, a segunda é uma verificação que
            aconteceu e não achou nada. O aviso vem do servidor, em texto fixo — nunca a mensagem
            de erro crua do SDK.
          */}
          {estado === 'FALHOU' && (
            <p className="erro-de-tela">
              {dados.exploracao.aviso ??
                'A exploração de hoje falhou. Esta seção não está afirmando que não há achados — ela não conseguiu verificar.'}
            </p>
          )}

          {emAndamento && !esperaEsgotada && (
            <p className="radar-candidatos-indisponivel">
              O assistente está analisando os dados de hoje. Esta seção atualiza sozinha assim que
              ele terminar.
            </p>
          )}

          {emAndamento && esperaEsgotada && (
            <p className="radar-candidatos-indisponivel">
              O assistente ainda está analisando. Recarregue a página em alguns minutos.
            </p>
          )}

          {estado === 'SEM_PROPOSTA' && (
            <p className="radar-candidatos-indisponivel">
              O assistente não propôs achados hoje. As consultas responderam normalmente.
            </p>
          )}

          {acompanhamentos.length > 0 && (
            <div className="radar-subsecao">
              <h4 className="radar-subsecao-titulo">Acompanhamentos aprovados pela equipe</h4>
              <p className="radar-subsecao-apoio">
                Estes recortes foram aprovados pela equipe e são atualizados todo dia, sem nova
                busca do assistente. Continue avaliando: se deixarem de ser úteis, eles saem.
              </p>
              <div className="radar-cards">
                {acompanhamentos.map((achado) => cartaoDe(achado, true))}
              </div>
            </div>
          )}

          {candidatos.length > 0 && (
            <div className={acompanhamentos.length > 0 ? 'radar-subsecao' : undefined}>
              {acompanhamentos.length > 0 && (
                <h4 className="radar-subsecao-titulo">Propostas novas de hoje</h4>
              )}
              <div className="radar-cards">
                {candidatos.map((achado) => cartaoDe(achado, false))}
              </div>
            </div>
          )}

          {/*
            O descarte é declarado. Corte silencioso deixaria o modelo errar sem ninguém saber —
            e faria a seção parecer completa quando não é.
          */}
          {dados.exploracao.candidatosDescartados > 0 && (
            <p className="radar-candidatos-indisponivel">
              {dados.exploracao.candidatosDescartados}{' '}
              {dados.exploracao.candidatosDescartados === 1
                ? 'proposta foi descartada'
                : 'propostas foram descartadas'}{' '}
              por não baterem com a consulta que deveria sustentá-las. O descarte é a proteção
              funcionando.
            </p>
          )}
        </>
      )}

      {achadoExplicado && (
        <ComoFunciona
          isOpen
          onClose={() => setAchadoExplicado(null)}
          titulo={`De onde vem: ${achadoExplicado.titulo}`}
          secoes={secoesDaProveniencia(achadoExplicado.proveniencia, comoConferir)}
        />
      )}
    </section>
  );
};

export default SecaoAchadosDoAssistente;
